// push namespace (window.backlit.push) — Web Push subscription management.
//
// The glow's app calls window.backlit.push.* to enroll the current signed-in
// user for push notifications. The platform owns the service worker (the SDK
// auto-registers /_backlit/sw.js on load — see sw-register.ts; do NOT register
// your own SW on a Backlit glow, it would clobber the platform one). This
// namespace drives the browser Push API and reports the subscription to the
// data-api, then stashes the re-subscribe coordinates in the IndexedDB the SW
// reads (backlit-sw.js) so a browser-side subscription rotation
// (pushsubscriptionchange) can re-register with no live page.
//
// Design notes (CLAUDE.md §8):
//   - supported() is the ONLY sync, never-throwing method on the whole SDK. It
//     is an honest capability probe (secure context + serviceWorker +
//     PushManager, plus the iOS "requires install" case); the app owns the
//     fallback UX. Off a glow host it returns { supported:false } rather than
//     throwing (contrast subscribe/state/etc., which throw unsupported_host).
//   - subscribe()/state()/unsubscribe()/test() are async and throw BacklitError
//     — unsupported_host off-glow, unsupported / requires_install when the
//     probe is false, permission_denied when the user declines, and the
//     server codes surfaced by the /_backlit/push/* endpoints.
//   - watch() is a client-side helper: it invokes the callback whenever the SW
//     posts backlit:notification-click or backlit:subscription-changed, and
//     whenever the OS notification permission changes.

import { BacklitError } from "./errors.js";
import { isBacklitHost } from "./host.js";
import { getBackend, type SubscribeResult } from "./backend.js";
import { idbGet, idbPutMany } from "./idb.js";

// PushSupport is what supported() returns — a synchronous, never-throwing
// capability probe. Callers gate their "enable notifications" UI on it.
//   - supported:       can this browser subscribe to push AT ALL right now?
//   - reason:          when supported is false, a short machine-ish tag for why
//                      ("off_host" | "insecure_context" | "no_service_worker" |
//                      "no_push_manager" | "requires_install"); omitted when
//                      supported is true.
//   - requiresInstall: this platform can only push once the app is installed to
//                      the home screen (iOS Safari). true implies the app is
//                      NOT yet installed (once installed, requiresInstall is
//                      false and supported flips true).
//   - installed:       whether the page is running as an installed
//                      (standalone/home-screen) app.
export interface PushSupport {
  supported: boolean;
  reason?: string;
  requiresInstall: boolean;
  installed: boolean;
}

// PushPermission mirrors the browser NotificationPermission tri-state.
export type PushPermission = "granted" | "denied" | "default";

// PushState is the current push state for this browser + user, from state().
//   - permission:  the OS notification permission ("granted"/"denied"/"default").
//   - subscribed:  whether this browser currently holds a push subscription.
//   - endpointId:  the server-side subscription id when subscribed (else "").
export interface PushState {
  permission: PushPermission;
  subscribed: boolean;
  endpointId: string;
}

// SubscribeOutcome is what subscribe() resolves to: the server-side
// subscription id and the (necessarily "granted") permission.
export interface SubscribeOutcome {
  endpointId: string;
  permission: "granted";
}

// WatchEvent is delivered to a watch() callback.
//   - type "notification-click":   the user tapped a delivered notification;
//                                   url is the notification's tap target.
//   - type "subscription-changed": the browser rotated the push subscription
//                                   and the SW re-registered it (no url).
//   - type "permission-changed":   the OS notification permission changed;
//                                   permission is the new value.
export type WatchEvent =
  | { type: "notification-click"; url: string }
  | { type: "subscription-changed" }
  | { type: "permission-changed"; permission: PushPermission };

export type WatchCallback = (event: WatchEvent) => void;

// Unsubscribe stops a watch() registration. Idempotent.
export type Unwatch = () => void;

export interface BacklitPush {
  /**
   * Synchronous capability probe — the ONLY sync, never-throwing SDK method.
   * Returns whether this browser can subscribe to push right now, and, when it
   * cannot, why. Off a deployed glow host it returns
   * `{ supported:false, requiresInstall:false, installed:false }` (it does not
   * throw `unsupported_host` — the async methods do). On iOS Safari (push works
   * only from an installed home-screen app) an uninstalled page reports
   * `{ supported:false, reason:"requires_install", requiresInstall:true,
   * installed:false }`. Gate your "Enable notifications" UI on `.supported`.
   */
  supported(): PushSupport;

  /**
   * Report the current push state for this browser + signed-in user: the OS
   * notification `permission`, whether this browser is currently `subscribed`,
   * and the server-side `endpointId` (or `""`). Requires a signed-in user
   * (throws `unauthenticated` otherwise) and a supported context.
   */
  state(): Promise<PushState>;

  /**
   * Subscribe the current signed-in user's browser to push. Requests
   * notification permission (throws `permission_denied` if declined), registers
   * the platform service worker, subscribes via the Push API using the glow's
   * VAPID key, reports the subscription to Backlit, and stashes the
   * re-subscribe coordinates for the service worker. Resolves the server-side
   * `endpointId` and `permission:"granted"`. Throws `unsupported` /
   * `requires_install` when {@link BacklitPush.supported} is false,
   * `unsupported_host` off a glow host, `unauthenticated` for an anonymous /
   * signed-out visitor, and the server push codes on failure.
   */
  subscribe(): Promise<SubscribeOutcome>;

  /**
   * Remove this browser's push subscription (both from the browser's PushManager
   * and from Backlit). A no-op if there is no active subscription. Requires a
   * signed-in user and a supported context.
   */
  unsubscribe(): Promise<void>;

  /**
   * Send a test push to the signed-in user's own subscriptions (a quick "did it
   * work" check for the enrolled user). Requires a signed-in user with at least
   * one active subscription (throws `not_found` if none). Throws
   * `unsupported_host` off a glow host.
   */
  test(): Promise<void>;

  /**
   * Register a callback for push-related client events: a notification tap
   * (`notification-click`, with the tap `url`), a service-worker-driven
   * subscription rotation (`subscription-changed`), and OS permission changes
   * (`permission-changed`). Returns an idempotent unsubscribe function. A no-op
   * (returns a no-op unwatch) off a glow host or in a non-browser context.
   */
  watch(callback: WatchCallback): Unwatch;
}

// PUSH_DB / PUSH_STORE / PUSH_DB_VERSION MUST byte-match cmd/edge/backlit-sw.js
// (DB_NAME / STORE / DB_VERSION) — the SW reads the four keys below from this
// exact database. A mismatch means the SW can never re-subscribe.
const PUSH_DB = "backlit-push";
const PUSH_STORE = "kv";
const PUSH_DB_VERSION = 1;

// The four stash keys the SW reads (backlit-sw.js). vapidPublicKey is the
// applicationServerKey as a Uint8Array (NOT the base64 string) so the SW can
// pass it straight to pushManager.subscribe without decoding.
const KEY_VAPID = "vapidPublicKey";
const KEY_REREGISTER_URL = "reRegisterUrl";
const KEY_REREGISTER_TOKEN = "reRegisterToken";
const KEY_ENDPOINT_ID = "endpointId";

// The SW's fixed re-subscribe endpoint (Slice 5/7). The server also advertises
// it in the /_backlit/ui push block; we use the advertised value when present
// and fall back to this canonical path.
const DEFAULT_REREGISTER_URL = "/_backlit/push/reregister";

// notInstalledOnIOS reports whether the platform is one where push requires an
// installed home-screen app AND the page is not currently running installed.
// The signal is iOS/iPadOS Safari: navigator.standalone === false and NOT in a
// standalone display-mode. We treat "matchMedia says standalone" OR
// "navigator.standalone === true" as installed. Best-effort — any probe error
// means "not the requires-install case" (fail toward the generic path).
function isInstalled(): boolean {
  try {
    const nav = navigator as Navigator & { standalone?: boolean };
    if (nav.standalone === true) return true;
    if (typeof matchMedia === "function") {
      return matchMedia("(display-mode: standalone)").matches === true;
    }
  } catch {
    /* ignore — treat as not installed */
  }
  return false;
}

// isIOSWebKit is a coarse UA sniff for iOS/iPadOS (where Web Push is gated
// behind home-screen install). We deliberately keep it narrow: the only place
// this changes behaviour is the requiresInstall path, and a false negative just
// yields the generic no_push_manager reason instead.
function isIOSWebKit(): boolean {
  try {
    const nav = navigator as Navigator & { maxTouchPoints?: number };
    const ua = (nav.userAgent ?? "").toLowerCase();
    if (/iphone|ipod|ipad/.test(ua)) return true;
    // iPadOS 13+ reports a desktop UA but is a touch Mac.
    if (/macintosh/.test(ua) && typeof nav.maxTouchPoints === "number" && nav.maxTouchPoints > 1) {
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

// probeSupport is the shared capability probe backing supported(). It never
// throws. Off a glow host it short-circuits to a not-supported result with no
// reason of its own so callers can distinguish "off host" from "on host but
// missing an API".
function probeSupport(): PushSupport {
  // Non-browser harness → not supported, but never throw.
  if (typeof navigator === "undefined" || typeof window === "undefined") {
    return { supported: false, reason: "no_service_worker", requiresInstall: false, installed: false };
  }
  if (!isBacklitHost()) {
    return { supported: false, reason: "off_host", requiresInstall: false, installed: false };
  }
  const installed = isInstalled();
  // Secure context is required for service workers + push.
  let secure = true;
  try {
    secure = window.isSecureContext !== false;
  } catch {
    secure = true; // fail open on a quirky harness
  }
  if (!secure) {
    return { supported: false, reason: "insecure_context", requiresInstall: false, installed };
  }
  const hasSW = "serviceWorker" in navigator;
  const hasPM = "PushManager" in window;
  if (!hasSW || !hasPM) {
    // iOS Safari exposes PushManager only inside an installed PWA. If we can see
    // it's iOS-WebKit and not installed, report the actionable requires-install
    // case rather than a bare "no PushManager".
    if (!installed && isIOSWebKit()) {
      return { supported: false, reason: "requires_install", requiresInstall: true, installed: false };
    }
    return {
      supported: false,
      reason: hasSW ? "no_push_manager" : "no_service_worker",
      requiresInstall: false,
      installed,
    };
  }
  return { supported: true, requiresInstall: false, installed };
}

// urlBase64ToUint8Array decodes a base64url (VAPID) public key into the raw
// bytes the Push API wants for applicationServerKey. VAPID keys are base64url
// (RFC 4648 §5, '-'/'_' , no padding); we restore standard base64 + padding and
// atob it. Throws BacklitError(internal) on a malformed key.
export function urlBase64ToUint8Array(base64url: string): Uint8Array {
  try {
    const padding = "=".repeat((4 - (base64url.length % 4)) % 4);
    const base64 = (base64url + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
  } catch (cause) {
    throw new BacklitError("internal", "the push VAPID key could not be decoded", 0, cause);
  }
}

// assertSupported throws the right BacklitError for a not-supported probe:
// unsupported_host off a glow host, requires_install on the iOS-install case,
// unsupported otherwise. Returns nothing on a supported probe.
function assertSupported(): void {
  const s = probeSupport();
  if (s.supported) return;
  if (s.reason === "off_host") {
    throw new BacklitError(
      "unsupported_host",
      "window.backlit.push.* only works on a deployed glow (*.backlit.run)",
    );
  }
  if (s.requiresInstall) {
    throw new BacklitError(
      "requires_install",
      "push notifications require installing this app to the home screen first",
    );
  }
  throw new BacklitError(
    "unsupported",
    `push notifications are not available in this browser (${s.reason ?? "unsupported"})`,
  );
}

// currentPermission reads Notification.permission defensively — a context
// without Notification returns "default".
function currentPermission(): PushPermission {
  try {
    if (typeof Notification === "undefined") return "default";
    return Notification.permission as PushPermission;
  } catch {
    return "default";
  }
}

export function createPush(): BacklitPush {
  return {
    supported(): PushSupport {
      // The one sync, never-throwing method. probeSupport swallows everything.
      return probeSupport();
    },

    async state(): Promise<PushState> {
      assertSupported();
      // state() is answered entirely client-side (there is no server "state"
      // endpoint): the OS Notification.permission, whether this browser holds a
      // live PushManager subscription, and the endpointId the SDK stashed on
      // the last subscribe(). Never throws for a plain "not subscribed" state.
      const permission = currentPermission();
      let subscribed = false;
      try {
        const registration = await navigator.serviceWorker.getRegistration("/");
        const sub = registration ? await registration.pushManager.getSubscription() : null;
        subscribed = sub !== null;
      } catch {
        /* ignore — treat an unreadable PushManager as "not subscribed" */
      }
      let endpointId = "";
      if (subscribed) {
        try {
          const stashed = await idbGet(PUSH_DB, PUSH_STORE, PUSH_DB_VERSION, KEY_ENDPOINT_ID);
          if (typeof stashed === "string") endpointId = stashed;
        } catch {
          /* ignore — the id is informational; "" is a fine fallback */
        }
      }
      return { permission, subscribed, endpointId };
    },

    async subscribe(): Promise<SubscribeOutcome> {
      assertSupported();

      // 1. Notification permission. requestPermission returns the resolved
      //    state; anything but "granted" is a client-side permission_denied.
      let permission: PushPermission;
      try {
        permission = (await Notification.requestPermission()) as PushPermission;
      } catch (cause) {
        throw new BacklitError("permission_denied", "notification permission request failed", 0, cause);
      }
      if (permission !== "granted") {
        throw new BacklitError("permission_denied", "notification permission was not granted");
      }

      // 2. Register (idempotent) + await the active platform SW.
      let registration: ServiceWorkerRegistration;
      try {
        registration = await navigator.serviceWorker.register("/_backlit/sw.js", { scope: "/" });
        await navigator.serviceWorker.ready;
      } catch (cause) {
        throw new BacklitError("internal", "the push service worker could not be registered", 0, cause);
      }

      // 3. Ask the server for the VAPID key + reregister coords, then subscribe
      //    via the Push API and report the subscription to Backlit.
      const cfg = await getBackend().uiConfig();
      if (!cfg.pushEnabled || cfg.vapidPublicKey.length === 0) {
        throw new BacklitError("unsupported", "push is not enabled for this glow");
      }
      const appServerKey = urlBase64ToUint8Array(cfg.vapidPublicKey);

      let subscription: PushSubscription;
      try {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          // The DOM lib types applicationServerKey as BufferSource<ArrayBuffer>;
          // our Uint8Array satisfies it at runtime. Cast through unknown to
          // sidestep the ArrayBufferLike-vs-ArrayBuffer generic mismatch (the
          // same escape hatch body.ts uses for CompressionStream).
          applicationServerKey: appServerKey as unknown as BufferSource,
        });
      } catch (cause) {
        throw new BacklitError("internal", "the browser could not create a push subscription", 0, cause);
      }

      const json = subscription.toJSON();
      const keys = json.keys ?? {};
      const p256dh = keys.p256dh ?? "";
      const auth = keys.auth ?? "";
      const result: SubscribeResult = await getBackend().pushSubscribe({
        endpoint: subscription.endpoint,
        keys: { p256dh, auth },
      });

      // 4. Stash the re-subscribe coordinates for the SW's
      //    pushsubscriptionchange handler (byte-aligned with backlit-sw.js).
      //    Best-effort: a stash failure does not fail the subscribe — the user
      //    IS subscribed; only silent re-subscribe after a rotation is lost.
      try {
        const stash: Array<[string, unknown]> = [
          [KEY_VAPID, appServerKey],
          [KEY_REREGISTER_URL, cfg.pushReregisterUrl || DEFAULT_REREGISTER_URL],
          [KEY_ENDPOINT_ID, result.endpointId],
        ];
        // The server returns an EMPTY reregisterToken on an idempotent
        // re-subscribe ("SW keeps its stored token"). Overwriting the previously
        // stashed valid token with "" would break the SW's pushsubscriptionchange
        // self-heal, so only stash a freshly minted (non-empty) token.
        if (result.reregisterToken) {
          stash.push([KEY_REREGISTER_TOKEN, result.reregisterToken]);
        }
        await idbPutMany(PUSH_DB, PUSH_STORE, PUSH_DB_VERSION, stash);
      } catch {
        /* ignore — the subscription is live; only the SW self-heal is degraded */
      }

      return { endpointId: result.endpointId, permission: "granted" };
    },

    async unsubscribe(): Promise<void> {
      assertSupported();
      // Best-effort browser-side unsubscribe first (so a subsequent subscribe
      // mints a fresh endpoint), then tell the server. We report the endpoint
      // we had so the server can prune the right row.
      let endpoint = "";
      try {
        const registration = await navigator.serviceWorker.getRegistration("/");
        const sub = registration ? await registration.pushManager.getSubscription() : null;
        if (sub) {
          endpoint = sub.endpoint;
          await sub.unsubscribe();
        }
      } catch {
        /* ignore — fall through to the server-side prune */
      }
      if (endpoint === "") return; // nothing to prune
      await getBackend().pushUnsubscribe({ endpoint });
    },

    async test(): Promise<void> {
      assertSupported();
      await getBackend().pushTest();
    },

    watch(callback: WatchCallback): Unwatch {
      // Non-browser / off-host → a no-op unwatch (never throws).
      if (typeof navigator === "undefined" || typeof window === "undefined" || !isBacklitHost()) {
        return () => {};
      }

      let active = true;

      // Service-worker → page messages (backlit-sw.js posts these).
      const onMessage = (ev: MessageEvent) => {
        if (!active) return;
        const data = ev.data as { type?: unknown; url?: unknown } | null | undefined;
        if (!data || typeof data.type !== "string") return;
        if (data.type === "backlit:notification-click") {
          callback({ type: "notification-click", url: typeof data.url === "string" ? data.url : "/" });
        } else if (data.type === "backlit:subscription-changed") {
          callback({ type: "subscription-changed" });
        }
      };
      try {
        navigator.serviceWorker.addEventListener("message", onMessage);
      } catch {
        /* ignore — no SW container */
      }

      // OS permission changes (permissions.query is best-effort / optional).
      let permStatus: PermissionStatus | null = null;
      const onPermChange = () => {
        if (!active || !permStatus) return;
        callback({ type: "permission-changed", permission: permStatus.state as PushPermission });
      };
      try {
        const perms = (navigator as Navigator & { permissions?: Permissions }).permissions;
        if (perms && typeof perms.query === "function") {
          void perms
            .query({ name: "notifications" as PermissionName })
            .then((status) => {
              if (!active) return;
              permStatus = status;
              status.addEventListener("change", onPermChange);
            })
            .catch(() => {
              /* ignore — permission observation is optional */
            });
        }
      } catch {
        /* ignore */
      }

      return () => {
        if (!active) return;
        active = false;
        try {
          navigator.serviceWorker.removeEventListener("message", onMessage);
        } catch {
          /* ignore */
        }
        if (permStatus) {
          try {
            permStatus.removeEventListener("change", onPermChange);
          } catch {
            /* ignore */
          }
          permStatus = null;
        }
      };
    },
  };
}
